import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6f4b3ghk82orf0ckqg";

type Status = "idle" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // 立即从真实表单读取数据（兼容浏览器自动填充）
    const formData = new FormData(form);
    const honeypot = String(formData.get("contact_alt") || "").trim();

    // 反垃圾：命中则静默视为成功，不发起请求
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");

    const payload = new URLSearchParams();
    payload.append("name", String(formData.get("name") || ""));
    payload.append("email", String(formData.get("email") || ""));
    payload.append("message", String(formData.get("message") || ""));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg: string =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="movie-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7"
    >
      <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">在线反馈</h2>
      <p className="mt-1.5 text-sm text-foreground-500">填写你的想法或问题，我们会认真阅读每一条留言。</p>

      <div className="mt-6 space-y-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            你的称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-accent-400 focus:ring-2 focus:ring-accent-200 outline-none"
          />
        </div>

        <div>
          <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱地址 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="方便我们回复你的邮箱"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-accent-400 focus:ring-2 focus:ring-accent-200 outline-none"
          />
        </div>

        <div>
          <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="fb-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="请描述你的建议、问题或想反馈的内容（最多 500 字）"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-accent-400 focus:ring-2 focus:ring-accent-200 outline-none resize-none"
          />
          <div className="mt-1.5 text-right text-xs text-foreground-400">{message.length} / 500</div>
        </div>

        <input
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="alt-contact-verify-field"
        />
      </div>

      {status === "success" && (
        <div className="mt-5 rounded-md bg-accent-100 border border-accent-300 text-accent-900 text-sm px-4 py-3 flex items-center gap-2 animate-fade-in">
          <i className="ri-checkbox-circle-line text-lg"></i>
          感谢你的反馈，我们已收到你的留言！
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-md bg-secondary-100 border border-secondary-300 text-secondary-900 text-sm px-4 py-3 flex items-center gap-2 animate-fade-in">
          <i className="ri-error-warning-line text-lg"></i>
          {formError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full sm:w-auto whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}